import { AbstractObserver } from 'utils/abstract-classes/abstract-observer'
import { deepMerge } from 'utils/object'
import { FEMVideoEvent } from './types'
import { JWPlayerConfigEvent, YTPlayerConfigEvent } from '../types'
import { VideoPlayerStore } from './store'
import { StickyWrapperEvent } from 'constants/enums'

// From Analytics
const CUSTOM_EVENT_TYPE = 'custom-event'
const VIDEO_EVENT_NAME = 'video'

/**
 * The common behaviour for each player.
 * This file is for having the common methods to control players.
 * For example, player.play() plays video no matter what the provider is.
 */
export abstract class AbstractPlayer extends AbstractObserver {
    protected _isAdPLaying = false
    protected videoId: string
    protected player: jwplayer.JWPlayer | YT.Player
    protected videoTitle: string

    // From Analytics
    protected playbackQuality: string
    protected isVolumeEnabled: boolean
    protected iframeData: FEMVideoEvent = {}

    public constructor(
        protected element: HTMLElement,
        protected events: YTPlayerConfigEvent | JWPlayerConfigEvent = {},
        protected isLeadVideo = false
    ) {
        super(element)
        VideoPlayerStore.getInstance().add(this)
        this.videoId = this.element.dataset.videoId || ''
        this.activateObserver()
    }

    /**
     * Type getter
     */
    public get type(): string {
        return this.element.dataset.position || ''
    }

    /**
     * Mute getter
     */
    public get mute(): boolean {
        if (this.element.dataset.mute === 'False') {
            return false
        }
        if (this.element.dataset.mute === 'True') {
            return true
        }
        return !!this.element.dataset.mute
    }

    /**
     * Mute setter
     * @param {boolean} mute
     */
    public set mute(mute: boolean) {
        this.element.dataset.mute = mute ? '1' : ''
        this.setMute(mute)
    }

    /**
     * Mute setter for each player
     * @throws {Error} Should be implemented
     */
    abstract setMute(mute: boolean): void

    /**
     * Controls getter
     */
    public get controls(): boolean {
        if (this.element.dataset.controls === 'False') {
            return false
        }
        if (this.element.dataset.controls === 'True') {
            return true
        }
        return !!this.element.dataset.controls
    }

    /**
     * Controls setter
     * @param {boolean} controls
     */
    public set controls(controls: boolean) {
        this.element.dataset.controls = controls ? '1' : ''
        this.setControls(controls)
    }

    /**
     * Controls setter for each player
     * @throws {Error} Should be implemented
     */
    abstract setControls(controls: boolean): void

    /**
     * Loop getter
     */
    public get loop(): boolean {
        if (this.element.dataset.loop === 'False') {
            return false
        }
        if (this.element.dataset.loop === 'True') {
            return true
        }
        return !!this.element.dataset.loop
    }

    /**
     * Loop setter
     * @param {boolean} loop
     */
    public set loop(loop: boolean) {
        this.element.dataset.loop = loop ? '1' : ''
        this.setLoop(loop)
    }

    /**
     * _isAdPLaying getter
     */
    public get isAdPlaying(): boolean {
        return this._isAdPLaying
    }

    /**
     * Loop setter for each player
     * @throws {Error} Should be implemented
     */
    abstract setLoop(loop: boolean): void

    /**
     * Autoplay getter
     */
    public get autoplay(): boolean {
        if (this.element.dataset.autoplay === 'False') {
            return false
        }
        if (this.element.dataset.autoplay === 'True') {
            return true
        }
        return !!this.element.dataset.autoplay
    }

    /**
     * Autoplay setter
     * @param {boolean} loop
     */
    public set autoplay(autoplay: boolean) {
        this.element.dataset.autoplay = autoplay ? '1' : ''
        this.setAutoplay(autoplay)
    }

    /**
     * Playlist type getter
     * to detect the playlist is contextual
     */
    protected get playlistType(): string {
        if (this.element.dataset.playlistType) {
            return this.element.dataset.playlistType
        }
        return ''
    }

    /**
     * Indicate the player is featured video.
     * NOTE: some player changes its attribute, so it fixes on the first loading
     */
    private _isFeatured: boolean | undefined
    public get isFeatured(): boolean {
        if (this._isFeatured === undefined) {
            this._isFeatured = this.type === 'featured'
        }
        return this._isFeatured
    }

    /**
     * Autoplay setter for each player
     * @throws {Error} Should be implemented
     */
    abstract setAutoplay(autoplay: boolean): void

    /**
     * Initialize the player.
     *
     * @todo Consider to merge into the constructor
     * @returns {Promise<void>}
     */
    abstract init(): Promise<void>

    /**
     * Get title.
     *
     * @returns {Error} Should be implemented
     */
    abstract get title(): string

    /**
     * Get the playlist index.
     */
    abstract get index(): number

    /**
     * Pause all other players using store.
     */
    public pauseAllOther(): void {
        // Hero video should not affect any other video's status
        if (this.type !== 'hero') {
            VideoPlayerStore.getInstance().pauseAllOther(this)
        }
    }

    /**
     * Pause the video.
     */
    abstract pause(): void

    /**
     * Play the video.
     */
    abstract play(): void

    /**
     * Play the video from its index from the list
     */
    abstract playVideoAt(_index: number): void

    /**
     * Destroy
     *
     * @throws {Error} Should be implemented
     */
    abstract destroy(): void

    /**
     * cuePlaylist
     *
     * @throws {Error} Should be implemented
     */
    abstract cuePlaylist(playlistArgs: YT.IPlaylistSettings): void

    abstract getState(): string

    /**
     * Player Pausable
     *
     * @throws {Error} Should be implemented
     */
    abstract isPausable(): boolean

    /**
     * Dispatch a 'video' custom event for event listener in FEM
     *
     * @param {FEMVideoEvent} eventData
     * @param {bool} isNonInteraction
     */
    private dispatchAnalytics(
        eventData: FEMVideoEvent,
        isNonInteraction = true
    ): void {
        let detail = deepMerge<FEMVideoEvent>(this.iframeData, eventData)

        if (this.videoId) {
            detail = deepMerge<FEMVideoEvent>(detail, {
                video_id: this.videoId,
                control_fields: {
                    mparticle: {
                        keys: {
                            video_id: 'video_id'
                        }
                    }
                }
            })
            if (this.videoTitle) {
                detail = deepMerge<FEMVideoEvent>(detail, {
                    video_name: this.videoTitle,
                    control_fields: {
                        mparticle: {
                            keys: {
                                video_name: 'video_name'
                            }
                        }
                    }
                })
            }
        }
        if (this.playbackQuality) {
            detail = deepMerge<FEMVideoEvent>(detail, {
                video_quality: this.playbackQuality,
                control_fields: {
                    mparticle: {
                        keys: {
                            video_quality: 'video_quality'
                        }
                    }
                }
            })
        }
        if (this.playlistType) {
            detail = deepMerge<FEMVideoEvent>(detail, {
                video_playlist_type: this.playlistType,
                control_fields: {
                    mparticle: {
                        keys: {
                            video_playlist_type: 'video_playlist_type'
                        }
                    }
                }
            })
        }
        if (this.isVolumeEnabled) {
            detail = deepMerge<FEMVideoEvent>(detail, {
                video_volume_enabled: `${this.isVolumeEnabled}`,
                control_fields: {
                    mparticle: {
                        keys: {
                            video_volume_enabled: 'video_volume_enabled'
                        }
                    }
                }
            })
        }
        detail = deepMerge<FEMVideoEvent>(detail, {
            video_autoplay: `${this.autoplay}`,
            video_user_interaction: String(!isNonInteraction),
            event_name: VIDEO_EVENT_NAME,
            control_fields: {
                mparticle: {
                    keys: {
                        video_autoplay: 'video_autoplay',
                        video_user_interaction: 'user_interaction'
                    },
                    mp_custom_flags: isNonInteraction
                        ? ['Google.NonInteraction', 'Google.Page']
                        : []
                }
            }
        })
        const event = new CustomEvent(CUSTOM_EVENT_TYPE, { detail })
        document.dispatchEvent(event)
    }

    /**
     * Dispatch play event for event listener in FEM
     *
     * @param {bool} isNonInteraction
     */
    protected dispatchAnalyticsPlay(isNonInteraction: boolean): void {
        this.dispatchAnalytics(
            {
                video_action: 'Video Start',
                control_fields: {
                    mparticle: {
                        keys: {
                            video_action: 'action'
                        }
                    },
                    permutive: {
                        keys: {
                            video_action: 'action',
                            video_user_interaction: 'user_interaction'
                        }
                    }
                }
            },
            isNonInteraction
        )
    }

    /**
     * Dispatch complete event for event listener in FEM
     */
    protected dispatchAnalyticsComplete(): void {
        this.dispatchAnalytics({
            video_action: 'Video Complete',
            control_fields: {
                mparticle: {
                    keys: {
                        video_action: 'action'
                    }
                },
                permutive: {
                    keys: {
                        video_action: 'action'
                    }
                }
            }
        })
    }

    /**
     * Dispatch paused event for event listener in FEM
     */
    protected dispatchAnalyticsPaused(): void {
        this.dispatchAnalytics(
            {
                video_action: 'Video Paused',
                control_fields: {
                    mparticle: {
                        keys: {
                            video_action: 'action'
                        }
                    }
                }
            },
            false
        )
    }

    /**
     * Dispatch error event for event listener in FEM
     *
     * @param {string} message
     */
    protected dispatchAnalyticsError(message: string): void {
        this.dispatchAnalytics({
            video_action: 'Video Error',
            video_error_message: message,
            control_fields: {
                mparticle: {
                    keys: {
                        video_action: 'action',
                        video_error_message: 'video_error_message'
                    }
                }
            }
        })
    }

    abstract onError(event: jwplayer.ErrorParam | YT.OnErrorEvent): void

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected onAdEnd(_event?: YT.PlayerEvent): void {
        this.dispatchAnalytics({
            video_action: 'Ad View Completed',
            control_fields: {
                mparticle: {
                    keys: {
                        video_action: 'action'
                    }
                }
            }
        })
    }

    /**
     * Dispatch ad start event for event listener in FEM
     */
    protected onAdStart(): void {
        this.dispatchAnalytics({
            video_action: 'Ad View Started',
            control_fields: {
                mparticle: {
                    keys: {
                        video_action: 'action'
                    }
                }
            }
        })
    }

    /**
     * Dispatch sticky restore event for event listener in FEM
     */
    public dispatchAnalyticsStickyRestore(): void {
        this.dispatchAnalytics(
            {
                video_action: 'Sticky Video Restored',
                control_fields: {
                    mparticle: {
                        keys: {
                            video_action: 'action'
                        }
                    }
                }
            },
            false
        )
    }

    /**
     * Dispatch sticky minimize event for event listener in FEM
     */
    public dispatchAnalyticsStickyMinimize(): void {
        this.dispatchAnalytics(
            {
                video_action: 'Sticky Video Minimized',
                control_fields: {
                    mparticle: {
                        keys: {
                            video_action: 'action'
                        }
                    }
                }
            },
            false
        )
    }

    /**
     * Dispatch sticky close event for event listener in FEM
     */
    public dispatchAnalyticsStickyClose(): void {
        this.dispatchAnalytics(
            {
                video_action: 'Sticky Video Closed',
                control_fields: {
                    mparticle: {
                        keys: {
                            video_action: 'action'
                        }
                    }
                }
            },
            false
        )
    }

    public changeStickyTitle(title?: string): void {
        if (!this.isFeatured) {
            return
        }

        const detail = { title: title ?? this.title }
        const event = new CustomEvent(StickyWrapperEvent.CHANGE_TITLE, {
            detail
        })
        document.dispatchEvent(event)
    }
}
